// Renders the LegalAcceptanceModal if and only if the user's
// users/{uid}.tos_accepted_version does NOT match the current LEGAL_VERSION.
//
// Modeled on PhoneVerificationGate's pattern: load on currentUser, gate the
// render on a loaded flag, perform writes via the modular Firestore SDK.
//
// Phase 1 only — no notice banner, no effective-date gate. See version.js
// PHASE 2 PROCEDURE comment block before bumping LEGAL_VERSION.

import React, { useCallback, useEffect, useState } from 'react';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../FirebaseConfig';
import LegalAcceptanceModal from './LegalAcceptanceModal';
import { LEGAL_VERSION, needsReAcceptance } from './version';

const LegalAcceptanceGate = ({ currentUser }) => {
  const [loaded, setLoaded] = useState(false);
  const [acceptedVersion, setAcceptedVersion] = useState(undefined);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', currentUser.uid));
        if (cancelled) return;
        if (snap.exists()) {
          setAcceptedVersion(snap.data().tos_accepted_version);
        } else {
          setAcceptedVersion(undefined);
        }
      } catch (err) {
        console.warn('LegalAcceptanceGate: user doc load failed', err);
        // Fail open on read error — better to let the user proceed than block
        // them out of the app on a transient Firestore error. The next page
        // load will retry.
        setAcceptedVersion(LEGAL_VERSION);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  const handleAccept = useCallback(async () => {
    if (!currentUser || accepting) return;
    setAccepting(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        tos_accepted_version: LEGAL_VERSION,
        tos_accepted_at: serverTimestamp(),
        tos_accepted_user_agent: navigator.userAgent,
      });
      setAcceptedVersion(LEGAL_VERSION);
    } catch (err) {
      console.error('LegalAcceptanceGate: failed to record acceptance', err);
      // Surface the error so the user knows something went wrong rather than
      // silently leaving them stuck on the modal.
      alert(
        'We could not record your acceptance. Please check your connection and try again. If the problem persists, email support@maiatech.ai.'
      );
    } finally {
      setAccepting(false);
    }
  }, [currentUser, accepting]);

  if (!loaded || !currentUser) return null;
  if (!needsReAcceptance(acceptedVersion, LEGAL_VERSION)) return null;

  const mode = acceptedVersion ? 'update' : 'first-time';
  return (
    <LegalAcceptanceModal
      mode={mode}
      onAccept={handleAccept}
      accepting={accepting}
    />
  );
};

export default LegalAcceptanceGate;
